import { useCallback, useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';

import type { TaskType } from '@/react-query/useGetTasks';
import { usePreferences } from '@/react-query/usePreferences';
import { useGetCountry } from '@/utils/internationalization/useGetCountry';
import {
  isPediatricToothId,
  universalPediatricToAdultMap,
} from '@videa/utils/src/normalizeToothIds';
import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import type {
  RecommendationFilter,
  SelectedCountsFilter,
} from '../../../../@types/appointments';
import type { PreferencesResponse } from '../../../../@types/preferences';
import {
  ArchProcedureNames,
  QuadProcedureNames,
  RecommendationToFeatureMap,
  type TProcedure,
  type TProcedureStatus,
  type TVisitSummary,
  type TVisitTreatment,
  ToothProcedureNames,
} from '../../../../react-query/useVisitSummaries';

import { permanentOnlyTeeth } from './constants';
import type { TSupportedVisualizations } from './Tooth';
import type { TDisplayOrderQuadrantAbbreviations } from './types';
import {
  LeftQuadrantAbbreviations,
  LowerQuadrantAbbreviations,
  RightQuadrantAbbreviations,
  UpperQuadrantAbbreviations,
} from './types';

export const useGetRecommendationName = (
  _shouldShowPerioParenthetical?: boolean,
) => {
  const { data: preferences } = usePreferences();
  const { t } = useLingui();
  const isCanadianPractice = useGetCountry() === 'CA';

  const shouldShowPerioParenthetical =
    preferences?.showParentheticalTxNames &&
    _shouldShowPerioParenthetical &&
    preferences.locSrpIndicatorEnabled &&
    preferences.locSrpOpportunity;

  const defaultRecommendationNames: Record<TProcedure['name'], string> =
    useMemo(() => {
      return {
        aligner: t`Clear Aligner`,
        crown: t`Crown`,
        enamelRegen: t`Incipient Tx`,
        extraction: t`Extraction`,
        filling: t`Filling`,
        implant: t`Implant`,
        locPerio: t`SRP (localized)`,
        other: t`Other`,
        perio: t`SRP`,
        pulpotomy: t`Pulpotomy`,
        rootCanal: t`Root Canal`,
      } as const;
    }, [t]);

  const getRecommendationName = useCallback(
    (procedureName: TProcedure['name']) => {
      const defaultNamePerio = isCanadianPractice
        ? t`Possible Perio`
        : defaultRecommendationNames[procedureName];

      const defaultNameLocPerio = isCanadianPractice
        ? t`Possible Perio (localized)`
        : defaultRecommendationNames[procedureName];

      // Helper function to map English indicatorCopy strings to translated versions
      const getTranslatedIndicatorCopy = (englishText: string): string => {
        switch (englishText) {
          // Tx oriented options
          case 'Crown':
            return t`Crown`;
          case 'Incipient Tx':
            return t`Incipient Tx`;
          case 'Filling':
            return t`Filling`;
          case 'SRP':
            return t`SRP`;
          case 'SRP (localized)':
            return t`SRP (localized)`;
          case 'Extraction':
            return t`Extraction`;
          case 'Implant':
            return t`Implant`;
          case 'Root Canal':
            return t`Root Canal`;
          case 'Pulpotomy':
            return t`Pulpotomy`;
          case 'Clear Aligner':
            return t`Clear Aligner`;
          // Dx oriented options
          case 'Significant Decay/Loss':
            return t`Significant Decay/Loss`;
          case 'Incipient Decay':
            return t`Incipient Decay`;
          case 'Decay/Loss into Dentin':
            return t`Decay/Loss into Dentin`;
          case 'Perio Assessment':
            return t`Perio Assessment`;
          case 'Perio Assessment (localized)':
            return t`Perio Assessment (localized)`;
          case 'Non-Restorable Tooth':
            return t`Non-Restorable Tooth`;
          case 'Unreplacable Tooth':
            return t`Unreplacable Tooth`;
          case 'Pulpal Involvement':
            return t`Pulpal Involvement`;
          case 'Pupal Involvement':
            return t`Pupal Involvement`;
          case 'Crowding/Spacing':
            return t`Crowding/Spacing`;
          // Canadian practice options
          case 'Possible Perio':
            return t`Possible Perio`;
          case 'Possible Perio (localized)':
            return t`Possible Perio (localized)`;
          case 'SRP (generalized)':
            return t`SRP (generalized)`;
          case 'Possible Perio (generalized)':
            return t`Possible Perio (generalized)`;
          default:
            return englishText; // Fallback to original text if not recognized
        }
      };

      // Extract ternary expressions into constants for better readability
      const crownName = preferences?.indicatorCopy?.crown
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.crown)
        : defaultRecommendationNames.crown;

      const enamelRegenName = preferences?.indicatorCopy?.incipient
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.incipient)
        : defaultRecommendationNames.enamelRegen;

      const extractionName = preferences?.indicatorCopy?.extraction
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.extraction)
        : defaultRecommendationNames.extraction;

      const rootCanalName = preferences?.indicatorCopy?.rootCanal
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.rootCanal)
        : defaultRecommendationNames.rootCanal;

      // TODO: Update pulpotomy indicator copy when supported by BE
      const pulpotomyName = defaultRecommendationNames.pulpotomy;

      const fillingName = preferences?.indicatorCopy?.filling
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.filling)
        : defaultRecommendationNames.filling;

      const implantName = preferences?.indicatorCopy?.implant
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.implant)
        : defaultRecommendationNames.implant;

      const alignerName = preferences?.indicatorCopy?.ortho
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.ortho)
        : defaultRecommendationNames.aligner;

      const perioBaseName = preferences?.indicatorCopy?.srp
        ? getTranslatedIndicatorCopy(preferences.indicatorCopy.srp)
        : defaultNamePerio;

      const perioName =
        perioBaseName + (shouldShowPerioParenthetical ? ' (generalized)' : '');

      const locPerioName = shouldShowPerioParenthetical
        ? preferences?.indicatorCopy?.locSrp
          ? getTranslatedIndicatorCopy(preferences.indicatorCopy.locSrp)
          : defaultNameLocPerio
        : preferences?.indicatorCopy?.srp
          ? getTranslatedIndicatorCopy(preferences.indicatorCopy.srp)
          : defaultNamePerio;

      switch (procedureName) {
        case 'crown':
          return crownName;
        case 'enamelRegen':
          return enamelRegenName;
        case 'extraction':
          return extractionName;
        case 'rootCanal':
          return rootCanalName;
        case 'pulpotomy':
          return pulpotomyName;
        case 'filling':
          return fillingName;
        case 'implant':
          return implantName;
        case 'aligner':
          return alignerName;
        case 'perio':
          return perioName;
        case 'locPerio':
          return locPerioName;
        default:
          return defaultRecommendationNames[procedureName] ?? procedureName;
      }
    },
    [
      defaultRecommendationNames,
      preferences?.indicatorCopy,
      isCanadianPractice,
      t,
      shouldShowPerioParenthetical,
    ],
  );

  return { getRecommendationName };
};

export const getIsEnabledByProcedureName = (
  name: TProcedure['name'],
  preferences?: PreferencesResponse,
): boolean => {
  switch (name) {
    case 'crown':
      return !!preferences?.crownOpportunity;
    case 'enamelRegen':
      return (
        !!preferences?.incipientIndicatorEnabled &&
        !!preferences?.incipientOpportunity
      );
    case 'extraction':
      return (
        !!preferences?.extractionIndicatorEnabled &&
        !!preferences.extractionOpportunity
      );
    case 'filling':
      return (
        !!preferences?.fillingIndicatorEnabled &&
        !!preferences.fillingOpportunity
      );
    case 'implant':
      return (
        !!preferences?.implantCrownIndicatorEnabled &&
        !!preferences.implantCrownOpportunity
      );
    case 'aligner':
      return (
        !!preferences?.alignerIndicatorEnabled &&
        !!preferences.alignerOpportunity
      );
    case 'perio':
      return (
        !!preferences?.srpIndicatorEnabled && !!preferences?.srpOpportunity
      );
    case 'locPerio':
      return (
        !!preferences?.locSrpIndicatorEnabled &&
        !!preferences?.locSrpOpportunity &&
        !!preferences?.srpIndicatorEnabled &&
        !!preferences?.srpOpportunity
      );
    case 'rootCanal':
      return (
        !!preferences?.rootCanalIndicatorEnabled &&
        !!preferences.rootCanalOpportunity
      );
    case 'pulpotomy':
      return (
        !!preferences?.pulpotomyIndicatorEnabled &&
        !!preferences.pulpotomyOpportunity
      );
    default:
      return false;
  }
};

/**
 * Checks if a perio procedure should be excluded for a perio patient.
 * Perio patients should not see perio or locPerio recommendations.
 */
export const shouldExcludePerioProcedureForPerioPatient = (
  name: TProcedure['name'],
  isPerioPatient?: boolean,
): boolean => {
  if (!isPerioPatient) return false;
  return name === 'perio' || name === 'locPerio';
};

export const getDoesTreatmentAddressRecommendation = (
  treatment: TVisitTreatment,
  procedures?: TProcedure[],
) => {
  return (procedures ?? []).some(({ name }) => {
    if (name === 'other') return false;

    const txFeatureKey = RecommendationToFeatureMap[name];
    if (!txFeatureKey) return false;

    const matchingTxFeature = treatment?.txFeatures?.find(
      (feature) => feature.name === txFeatureKey,
    );
    const correlation = matchingTxFeature?.correlation;

    switch (treatment.status) {
      case 'completed':
        return !!matchingTxFeature;
      // There's a completed treatment with any correlation to the current
      // recommendation (over-treatment, under-treatment, or exact match).
      case 'scheduled_today':
      case 'scheduled_future':
        return typeof correlation === 'number' && correlation >= 0;
      // There's a scheduled treatment that is an over-treatment or exact match
      // to the current recommendation.
      default:
        return false;
    }
  });
};

export const getIsRecommendationAddressedByTreatment = (
  procedure: TProcedure,
  treatments?: TVisitTreatment[],
  isPerioPatient?: boolean,
) => {
  if (!treatments || !procedure.recommended || procedure.name === 'other') {
    // In reality, there should be no "other" recommendations.
    return false;
  }

  const txFeatureKey = RecommendationToFeatureMap[procedure.name];
  if (!txFeatureKey) return false;

  return treatments.some((treatment) => {
    const matchingTxFeature = treatment?.txFeatures?.find(
      (feature) => feature.name === txFeatureKey,
    );
    const correlation = matchingTxFeature?.correlation;

    switch (treatment.status) {
      case 'completed':
        return !!matchingTxFeature;
      // There's a completed treatment with any correlation to the current
      // recommendation (over-treatment, under-treatment, or exact match).
      case 'scheduled_today':
      case 'scheduled_future':
        return typeof correlation === 'number' && correlation >= 0;
      // There's a scheduled treatment that is an over-treatment or exact match
      // to the current recommendation.
      default:
        return false;
    }
  });
};

const getIsTooth = (
  visitSummaryArea?:
    | TVisitSummary['teeth' | 'quadrants' | 'arches'][number]
    | TVisitSummary['mouth'],
): visitSummaryArea is TVisitSummary['teeth'][number] => {
  return typeof (visitSummaryArea as any)?.toothId === 'string';
};

const getIsArch = (
  visitSummaryArea?:
    | TVisitSummary['teeth' | 'quadrants' | 'arches'][number]
    | TVisitSummary['mouth'],
): visitSummaryArea is TVisitSummary['arches'][number] => {
  return typeof (visitSummaryArea as any)?.arch === 'string';
};

export interface TProcedureWithAddressed extends TProcedure {
  isAddressed?: boolean;
  gapDist?: number;
  hasCrowding?: boolean;
  summedToothSpacing?: number;
}

export const getRecommendedProcedures = (
  visitSummaryArea?:
    | TVisitSummary['teeth' | 'quadrants' | 'arches'][number]
    | TVisitSummary['mouth'],
  preferences?: PreferencesResponse,
  shouldExcludeAddressedOpportunities?: boolean,
  isPerioPatient?: boolean,
) => {
  if (!visitSummaryArea) return [];

  // Aggregate treatments from all procedures because a treatment under a
  // different procedure group (feature) might address the recommendation
  const allTreatments = visitSummaryArea.procedures.flatMap((procedure) =>
    Object.values(procedure.treatments).flat(),
  );

  return visitSummaryArea.procedures.reduce((acc, procedure) => {
    const { name, recommended: isRecommended } = procedure;
    const isPreferenceEnabled = getIsEnabledByProcedureName(name, preferences);
    const shouldExcludeForPerioPatient =
      shouldExcludePerioProcedureForPerioPatient(name, isPerioPatient);
    if (
      !isRecommended ||
      !isPreferenceEnabled ||
      shouldExcludeForPerioPatient
    ) {
      return acc;
    }

    const isRecommendationAddressedByTreatment =
      getIsRecommendationAddressedByTreatment(
        procedure,
        allTreatments,
        isPerioPatient,
      );

    if (
      shouldExcludeAddressedOpportunities &&
      isRecommendationAddressedByTreatment
    ) {
      return acc;
    }

    return acc.concat({
      ...procedure,
      isAddressed: isRecommendationAddressedByTreatment,
      gapDist: getIsTooth(visitSummaryArea)
        ? visitSummaryArea.gapDist
        : undefined,
      hasCrowding: getIsArch(visitSummaryArea)
        ? visitSummaryArea.hasCrowding
        : undefined,
      summedToothSpacing: getIsArch(visitSummaryArea)
        ? visitSummaryArea.summedToothSpacing
        : undefined,
    });
  }, [] as TProcedureWithAddressed[]);
};

export const isUpperQuadrant = (
  quadrantAbbreviation: TDisplayOrderQuadrantAbbreviations,
) =>
  (UpperQuadrantAbbreviations as readonly string[]).includes(
    quadrantAbbreviation,
  );
export const isLowerQuadrant = (
  quadrantAbbreviation: TDisplayOrderQuadrantAbbreviations,
) =>
  (LowerQuadrantAbbreviations as readonly string[]).includes(
    quadrantAbbreviation,
  );
export const isRightQuadrant = (
  quadrantAbbreviation: TDisplayOrderQuadrantAbbreviations,
) =>
  (RightQuadrantAbbreviations as readonly string[]).includes(
    quadrantAbbreviation,
  );
export const isLeftQuadrant = (
  quadrantAbbreviation: TDisplayOrderQuadrantAbbreviations,
) =>
  (LeftQuadrantAbbreviations as readonly string[]).includes(
    quadrantAbbreviation,
  );

export const isPermanentOnlyTooth = (toothId: string) =>
  (permanentOnlyTeeth as readonly string[]).includes(toothId);
export const hasPermanentOnlyTeeth = (visitSummary?: TVisitSummary) =>
  !!visitSummary?.teeth.some(({ toothId }) => isPermanentOnlyTooth(toothId));

export const hasPrimaryTeeth = (visitSummary?: TVisitSummary) =>
  !!visitSummary?.teeth.some(
    ({ toothId }) => toothId !== 'missing' && isPediatricToothId(toothId),
  );

export const getMatchingPrimaryTooth = (
  toothId: ValidUniversalToothId,
  visitSummary?: TVisitSummary,
) => {
  if (isPediatricToothId(toothId)) return;

  return visitSummary?.teeth?.find((tooth) => {
    if (!isPediatricToothId(tooth.toothId)) return false;

    const matchingPermanentTooth =
      universalPediatricToAdultMap[
        tooth.toothId as keyof typeof universalPediatricToAdultMap
      ];

    return String(matchingPermanentTooth) === toothId;
  });
};

export const isTaskFilter = (name?: SelectedCountsFilter): name is TaskType => {
  const taskFilters: TaskType[] = [
    'BW_DUE',
    'FLUORIDE_DUE',
    'FMX_DUE',
    'PERIO_CHART_DUE',
    'PERIO_MAINTENANCE_DUE',
    'PROPHY_DUE',
    'SCAN_OPPORTUNITY',
  ];
  return !!name && (taskFilters as readonly string[]).includes(name);
};

export const isRecommendationFilter = (
  name?: SelectedCountsFilter,
): name is RecommendationFilter => {
  // Build array of recommendation filters dynamically from constants
  const recommendationFilters: RecommendationFilter[] = [
    ...Object.values(ToothProcedureNames),
    ...Object.values(QuadProcedureNames),
    ...Object.values(ArchProcedureNames),
    'ALL_OPPORTUNITIES',
  ];
  return !!name && (recommendationFilters as readonly string[]).includes(name);
};

export const getIsVisualizationFilteredOut = (
  name?: TSupportedVisualizations | TProcedure['name'],
  status?: TProcedureStatus,
  selectedCountsFilter?: SelectedCountsFilter,
  module?: 'image-viewer' | 'patient-list',
) => {
  if (
    !isRecommendationFilter(selectedCountsFilter) ||
    module === 'image-viewer'
  ) {
    return false;
  }

  const isOpportunity = status === 'recommended';
  const isMatchingFilter =
    isOpportunity &&
    (selectedCountsFilter === name ||
      selectedCountsFilter === 'ALL_OPPORTUNITIES' ||
      (selectedCountsFilter === 'perio' && name === 'locPerio') ||
      (selectedCountsFilter === 'rootCanal' && name === 'pulpotomy'));
  return !isMatchingFilter;
};
